import React from 'react'
import styled from 'styled-components'
import { darken } from 'polished'

import { RowBetween } from '../../../components/Row'
import { TYPE } from '../../../theme'
import { MAX_DAYS, MIN_DAYS } from '../RedeemInput'

const LabelRow = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  color: ${({ theme }) => theme.text1};
  font-size: 0.75rem;
  line-height: 1rem;
  padding: 1.125rem 1.125rem 0 1.125rem;
  span:hover {
    cursor: pointer;
    color: ${({ theme }) => darken(0.2, theme.text2)};
  }
`

const Aligner = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 18px 18px 18px;
`

const InputPanel = styled.div<{ hideInput?: boolean }>`
  ${({ theme }) => theme.flexColumnNoWrap}
  position: relative;
  border-radius: 14px;
  z-index: 1;
  flex: 1;
`

const Container = styled.div`
  border-radius: 14px;
  background-color: ${({ theme }) => theme.bg1};
  border: 1px solid ${({ theme }) => theme.bg1};
  &:hover {
    border: 1px solid ${({ theme }) => theme.bg3};
  }
`

const ButtonStyled = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 300ms;
  border-radius: 0.5rem;
  padding: 8px;

  &:hover {
    background-color: #565a691a;
  }
`

const InputContainerStyled = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;

  input {
    all: unset;
    color: #4afc38;
    font-size: 16px;
    width: 36px;
    text-align: center;
    height: 36px;
    border: 1px solid lightgrey;
    border-radius: 8px;
  }
`
const InputValueStyled = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  color: lightgrey;
`
const StyledBalanceMax = styled.button`
  background-color: transparent;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  padding: 0;
  margin-left: 6px;
  font-weight: 400;
  cursor: pointer;
  color: #aeff9a;

  :hover {
    color: ${({ theme }) => theme.primaryText1};
  }
  :focus {
    border: 1px solid ${({ theme }) => theme.primary1};
    outline: none;
  }

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    margin-right: 0.5rem;
  `};
`

interface DurationInputNumberProps {
  setValue: (value: any) => void
}

export default function DurationInputNumberFork({ setValue }: DurationInputNumberProps) {
  const [months, setMonths] = React.useState<number>(0)
  const [days, setDays] = React.useState<number>(MIN_DAYS)

  const addDay = () => {
    if (months * 30 + days + 1 <= MAX_DAYS) {
      setDays(days === 30 ? 0 : days + 1)
      if (days === 30) setMonths(months + 1)
    }
  }

  const removeDay = () => {
    if (days === 0 && months > 0) {
      setMonths(months - 1)
      setDays(30)
    } else {
      setDays(Math.max(days - 1, 0))
    }
  }

  const handleDays = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputDays = parseInt(e.target.value.replace(/^0+/, ''), 10)

    if (isNaN(inputDays) || inputDays < 0) {
      setDays(0)
      return
    }

    // Calculate the total days considering the current months
    let totalDays = months * 30 + inputDays

    // If the total days exceed MAX_DAYS, clamp it to MAX_DAYS
    if (totalDays > MAX_DAYS) {
      totalDays = MAX_DAYS
    }

    // Calculate the new values for months and days
    const newMonths = Math.floor(totalDays / 30)
    const newDays = totalDays % 30

    setMonths(newMonths)
    setDays(newDays)
  }
  const handleMonths = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputMonths = parseInt(e.target.value.replace(/^0+/, ''), 10)
    if (isNaN(inputMonths) || inputMonths < 0) {
      setMonths(0)
      return
    }
    if (inputMonths > 6) {
      setMonths(6)
      return
    }
    if (inputMonths * 30 + days > MAX_DAYS) {
      setMonths(inputMonths)
      return
    }

    setMonths(inputMonths)
  }

  React.useEffect(() => {
    setValue(months * 30 + days)
  }, [days, months, setValue])

  return (
    <>
      <InputPanel>
        <Container>
          <LabelRow>
            <RowBetween>
              <TYPE.body color={'#8B8B8B'} fontWeight={400} fontSize={12}>
                Redeem Duration
              </TYPE.body>
              <div>
                <StyledBalanceMax
                  onClick={() => {
                    setMonths(0)
                    setDays(MIN_DAYS)
                  }}
                >
                  MIN
                </StyledBalanceMax>
                <StyledBalanceMax
                  onClick={() => {
                    setMonths(6)
                    setDays(0)
                  }}
                >
                  MAX
                </StyledBalanceMax>
              </div>
            </RowBetween>
          </LabelRow>
          <Aligner>
            <ButtonStyled onClick={() => removeDay()}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#4afc38"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M5 12l14 0" />
              </svg>
            </ButtonStyled>
            <InputContainerStyled>
              <InputValueStyled>
                <input value={months} onChange={handleMonths} /> Months
              </InputValueStyled>
              <InputValueStyled>
                <input value={days} onChange={handleDays} /> Days
              </InputValueStyled>
            </InputContainerStyled>
            <ButtonStyled onClick={() => addDay()}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#4afc38"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M12 5l0 14" />
                <path d="M5 12l14 0" />
              </svg>
            </ButtonStyled>
          </Aligner>
        </Container>
      </InputPanel>
    </>
  )
}
